import { EmptyState, ErrorNote, Spinner } from '../ui/primitives.js';
// Transcript in meeting-timeline order as a single-select listbox: a click or
// the keyboard (arrows, Home, End, Enter, Space) selects and highlights a
// segment; nothing plays or seeks. Track-relative times say so.
import { useEffect, useRef } from 'react';
import type { KeyboardEvent } from 'react';
import type { WebSegment } from '../api/types.js';
import { COPY } from '../copy.js';
import { formatClock, speakerLabel } from '../ui/labels.js';
import type { Paged } from './usePaged.js';

/**
 * The meeting-timeline segment containing `meetingMs` (the latest-starting one
 * when segments overlap), or null. Track-relative segments never match: their
 * times are not meeting time.
 */
export function segmentAtMeetingMs(segments: readonly WebSegment[], meetingMs: number): WebSegment | null {
  let found: WebSegment | null = null;
  for (const s of segments) {
    if (s.timeline !== 'meeting' || s.start_ms > meetingMs) continue;
    if (meetingMs < s.end_ms || meetingMs === s.start_ms) found = s;
  }
  return found;
}

const optionId = (id: string) => `segment-option-${id}`;

export function Transcript({
  segments,
  selectedId,
  onSelect,
  revealId = null,
}: {
  segments: Paged<WebSegment>;
  selectedId: string | null;
  onSelect: (segment: WebSegment) => void;
  /** Scrolled into view when it changes (the `?t=` selection). */
  revealId?: string | null;
}) {
  const list = useRef<HTMLOListElement>(null);
  const items = segments.items;
  // Roving tabindex: the selected segment, or the first, is the one Tab reaches.
  const tabbable = items.some((s) => s.id === selectedId) ? selectedId : (items[0]?.id ?? null);

  useEffect(() => {
    if (revealId === null) return;
    list.current?.ownerDocument.getElementById(optionId(revealId))?.scrollIntoView?.({ block: 'center' });
  }, [revealId]);

  const selectAt = (index: number) => {
    const s = items[index];
    if (!s) return;
    onSelect(s);
    list.current?.ownerDocument.getElementById(optionId(s.id))?.focus();
  };

  const onKeyDown = (event: KeyboardEvent<HTMLOListElement>) => {
    const current = items.findIndex((s) => optionId(s.id) === (event.target as HTMLElement).id);
    if (current < 0) return;
    const next: Record<string, number> = {
      ArrowDown: Math.min(items.length - 1, current + 1),
      ArrowUp: Math.max(0, current - 1),
      Home: 0,
      End: items.length - 1,
      Enter: current,
      ' ': current,
    };
    const target = next[event.key];
    if (target === undefined) return;
    event.preventDefault();
    selectAt(target);
  };

  return (
    <section aria-labelledby="transcript-title" className="transcript">
      <h2 id="transcript-title">{COPY.transcript}</h2>
      {items.length === 0 && !segments.loading && !segments.error && <EmptyState title={COPY.transcriptEmpty} />}
      {items.length > 0 && (
        <ol
          ref={list}
          className="segments"
          role="listbox"
          aria-label={COPY.transcriptSegments}
          data-testid="segments"
          onKeyDown={onKeyDown}
        >
          {items.map((s, i) => {
            const selected = s.id === selectedId;
            return (
              <li
                key={s.id}
                id={optionId(s.id)}
                role="option"
                aria-selected={selected}
                tabIndex={s.id === tabbable ? 0 : -1}
                className={selected ? 'segment segment--selected' : 'segment'}
                data-testid={`segment-${s.id}`}
                onClick={() => selectAt(i)}
              >
                <span className="segment__head">
                  <span className="segment__time">{formatClock(s.start_ms)}</span>{' '}
                  <span className="segment__speaker">{speakerLabel(s.speaker_kind, s.speaker)}</span>
                  {s.timeline === 'track' && <span className="muted small"> &#183; {COPY.trackRelative}</span>}
                </span>
                <p className="segment__text">{s.text}</p>
              </li>
            );
          })}
        </ol>
      )}
      {segments.loading && <div className="loading"><Spinner /><span>{COPY.loading}</span></div>}
      {segments.error && <ErrorNote message={segments.error} />}
      {segments.hasMore && !segments.loading && (
        <button type="button" onClick={segments.loadMore}>
          {COPY.loadMore}
        </button>
      )}
    </section>
  );
}
